import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from '@/lib/auth/use-auth'
import { useReference } from '@/lib/reference/use-reference'
import type { Timezone } from '@/lib/reference'
import { formatLocal, formatOffset, formatUtc, zoneStateAt, type ZoneRules } from '@/lib/time'
import { ReferenceTable, type Column } from '@/components/reference/ReferenceTable'
import { Badge } from '@/components/ui/Badge'

/**
 * IANA zones plus the clock changes each one goes through.
 *
 * The offset column is the value at the start of the shipped window, not
 * "the" offset: a zone that observes DST does not have one. The panel on the
 * right is where the real answer lives — every change the zone makes inside
 * the window, which is what turns a UTC instant into a local time on the
 * Gantt.
 */
export function TimezonesPage() {
  const { can } = useAuth()
  const { timezones, airports, zoneRules, manifest, create, update, remove } = useReference()
  const [selected, setSelected] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(timer)
  }, [])

  const airportsPerZone = useMemo(() => {
    const counts = new Map<string, number>()
    for (const airport of airports) {
      counts.set(airport.timezone, (counts.get(airport.timezone) ?? 0) + 1)
    }
    return counts
  }, [airports])

  const columns = useMemo<Column<Timezone>[]>(
    () => [
      { key: 'name', header: 'Zone', value: (row) => row.name, editor: 'text', isKey: true },
      {
        key: 'countryCode',
        header: 'Country',
        value: (row) => row.countryCode,
        editor: 'text',
        cellClassName: 'w-24',
        render: (row) => <span className="t-number-sm text-ink">{row.countryCode || '—'}</span>,
      },
      {
        key: 'abbreviation',
        header: 'Abbr',
        value: (row) => row.abbreviation,
        editor: 'text',
        cellClassName: 'w-24',
      },
      {
        key: 'baseOffset',
        header: 'Offset',
        value: (row) => row.baseOffset,
        render: (row) => formatOffset(row.baseOffset),
        editor: 'number',
        numeric: true,
        filter: 'none',
        cellClassName: 'w-28',
      },
      {
        key: 'observesDst',
        header: 'DST',
        value: (row) => (row.observesDst ? 'Yes' : 'No'),
        filter: 'select',
        options: ['Yes', 'No'],
        cellClassName: 'w-20',
        render: (row) =>
          row.observesDst ? <span className="text-ink">Yes</span> : <span className="text-muted-soft">No</span>,
      },
      {
        key: 'transitionCount',
        header: 'Changes',
        value: (row) => row.transitionCount,
        numeric: true,
        filter: 'none',
        cellClassName: 'w-24 text-right',
      },
      {
        key: 'airports',
        header: 'Airports',
        value: (row) => airportsPerZone.get(row.name) ?? 0,
        numeric: true,
        filter: 'none',
        cellClassName: 'w-24 text-right',
      },
    ],
    [airportsPerZone],
  )

  const selectedZone = selected ? timezones.find((row) => row.name === selected) ?? null : null

  return (
    <section>
      <h1 className="t-title-lg text-ink">Timezones</h1>
      {manifest && (
        <p className="t-caption mt-2 text-muted">
          Clock changes {manifest.window.from.slice(0, 4)}–{manifest.window.to.slice(0, 4)}
        </p>
      )}

      <div
        className={`mt-6 grid gap-6 ${
          selectedZone ? 'xl:grid-cols-[minmax(0,1fr)_380px]' : 'grid-cols-1'
        }`}
      >
        <ReferenceTable
          columns={columns}
          rows={timezones}
          entity="Timezone"
          canEdit={can('settings.edit')}
          selectedId={selectedZone?.id ?? null}
          onSelect={(row) => setSelected((current) => (current === row.name ? null : row.name))}
          onCreate={(values) =>
            create('timezone', {
              name: values.name.trim(),
              countryCode: values.countryCode.trim().toUpperCase(),
              abbreviation: values.abbreviation.trim(),
              baseOffset: Number(values.baseOffset),
              observesDst: false,
              transitionCount: 0,
              distinctOffsets: 1,
            })
          }
          onUpdate={(id, values) =>
            update('timezone', id, {
              name: values.name.trim(),
              countryCode: values.countryCode.trim().toUpperCase(),
              abbreviation: values.abbreviation.trim(),
              baseOffset: Number(values.baseOffset),
            })
          }
          onDelete={(id) => remove('timezone', id)}
        />

        {selectedZone && (
          <ZonePanel
            zone={selectedZone}
            rules={zoneRules[selectedZone.name]}
            now={now}
            onClose={() => setSelected(null)}
          />
        )}
      </div>
    </section>
  )
}

/** Every clock change the zone makes inside the shipped window. */
function ZonePanel({
  zone,
  rules,
  now,
  onClose,
}: {
  zone: Timezone
  rules: ZoneRules | undefined
  now: number
  onClose: () => void
}) {
  const state = rules ? zoneStateAt(zone.name, rules, now) : null

  // Each change is shown as the local clock either side of it, which is the
  // form an ops planner reads: "02:00 becomes 03:00".
  const changes = useMemo(() => {
    if (!rules) return []
    let previous = rules.b[1]
    return rules.t.map(([at, abbreviation, offset, dst]) => {
      const row = {
        at: at * 1000,
        abbreviation,
        offset,
        dst: dst === 1,
        before: previous,
        shift: offset - previous,
      }
      previous = offset
      return row
    })
  }, [rules])

  const byYear = useMemo(() => {
    const groups = new Map<number, typeof changes>()
    for (const change of changes) {
      const year = new Date(change.at).getUTCFullYear()
      const bucket = groups.get(year)
      if (bucket) bucket.push(change)
      else groups.set(year, [change])
    }
    return [...groups.entries()]
  }, [changes])

  return (
    <aside className="rounded-[24px] border border-hairline p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="t-title-md truncate text-ink">{zone.name}</h2>
          <p className="t-caption mt-1 text-muted">{zone.countryCode || '—'}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          className="rounded-[8px] p-1.5 text-body transition-colors hover:bg-surface-strong"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {!rules ? (
        <p className="t-body-sm mt-6 text-semantic-down">No rules in the transition table.</p>
      ) : (
        <>
          <dl className="mt-6 space-y-3">
            <Row label="Local now">{state ? formatLocal(now, state.offsetSeconds) : '—'}</Row>
            <Row label="Offset">{state ? formatOffset(state.offsetSeconds) : '—'}</Row>
            <Row label="Abbreviation">{state?.abbreviation ?? '—'}</Row>
            <Row label="DST">
              {state?.isDst ? <Badge>On</Badge> : <span className="text-muted-soft">Off</span>}
            </Row>
            <Row label="Next change">
              {state?.until ? formatUtc(state.until) : <span className="text-muted-soft">None</span>}
            </Row>
          </dl>

          <div className="mt-8 border-t border-hairline-soft pt-5">
            <div className="flex items-baseline justify-between">
              <h3 className="t-title-sm text-ink">Clock changes</h3>
              <span className="t-number-sm text-muted">{changes.length}</span>
            </div>

            {changes.length === 0 ? (
              <p className="t-body-sm mt-4 text-muted">Fixed offset.</p>
            ) : (
              <div className="mt-4 max-h-[420px] overflow-y-auto pr-1">
                {byYear.map(([year, rows]) => (
                  <div key={year} className="mb-4 last:mb-0">
                    <p className="t-caption-strong sticky top-0 bg-canvas py-1 text-muted">{year}</p>
                    <ul className="mt-1 space-y-1">
                      {rows.map((change) => (
                        <li
                          key={change.at}
                          className="flex items-baseline justify-between gap-3 border-b border-hairline-soft pb-1.5 last:border-0"
                        >
                          <span className="t-number-sm text-ink">
                            {formatLocal(change.at, change.before).slice(5)}
                          </span>
                          <span className="t-caption text-muted">
                            {formatOffset(change.before)} → {formatOffset(change.offset)}
                          </span>
                          <span
                            className={`t-caption-strong ${
                              change.shift > 0 ? 'text-semantic-up' : 'text-semantic-down'
                            }`}
                          >
                            {change.shift > 0 ? '+' : '−'}
                            {Math.abs(change.shift) / 60}m
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </aside>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="t-caption text-muted">{label}</dt>
      <dd className="t-body-sm text-ink">{children}</dd>
    </div>
  )
}
